import CanvasEyedropper from "./CanvasEyedropper";
import DPR from "./DPR";
import DragCamera from "./DragCamera";
import DragResizer from "./DragResizer";
import DragToolUsable from "./DragToolUsable";
import GrabbableWindow from "./GrabbableWindow";
import ImageResizer from "./ImageResizer";
import { PopupMenu } from "./PreviewWindow";

export default class ExampleWindowContainer {
  constructor(mulLangTexts, autoFocus, eyedropperPopup, colorManager) {
    this._mulLangTexts = mulLangTexts;
    this._autoFocus = autoFocus;
    this._eyedropperPopup = eyedropperPopup;
    this._colorManager = colorManager;
    this._windows = [];
  }
  addWindow(buttons, keyboard) {
    navigator.clipboard.read().then((clipboardItems) => {
      for (const clipboardItem of clipboardItems) {
        for (const type of clipboardItem.types) {
          if (type.startsWith('image/')) {
            clipboardItem.getType(type).then((blob) => {
              this._addWindow(URL.createObjectURL(blob), buttons, keyboard);
            });
          } else if (type === 'text/plain') {
            clipboardItem.getType(type).then((blob) => {
              const reader = new FileReader();
              reader.onload = () => {
                const text = reader.result;
                if (text.match(/^https?:\/\//)) {
                  this._addWindow(text, buttons, keyboard);
                }
              };
              reader.readAsText(blob);
            });
          }
        }
      }
    });
  }
  _addWindow(url, buttons, keyboard) {
    new Promise((resolve) => {
      const image = new Image();
      image.src = url;
      image.onload = () => {
        resolve({ image });
      };
    }).then(({ image }) => {
      this._windows.push(new ExampleWindow((self) => {
        this._windows = this._windows.filter((exampleWindow) => exampleWindow !== self);
        // console.log(this._windows.length);
      }, image, buttons, keyboard, this._mulLangTexts, this._autoFocus, this._eyedropperPopup, this._colorManager));
    });
  }
}

class ExampleWindow {
  static get GRABBER_SIZE() {
    return 32;
  }
  static get MIN_SIZE() {
    return this.GRABBER_SIZE * 2;
  }

  constructor(ondestroy, image, buttons, keyboard, mulLangTexts, autoFocus, eyedropperPopup, colorManager) {
    this._ondestroy = ondestroy;
    this._image = image;
    this._keyboard = keyboard;
    this._mulLangTexts = mulLangTexts;

    this._elWrapper = document.createElement('div');
    this._elWrapper.className = 'exampleWindowWrapper';
    this._elWrapper.dataset.autoFocusToChildren = '1';
    this._elWrapper.dataset.help = 'example-window';
    this._elWrapper.innerHTML = `
      <section class="exampleWindow floatingWindow">
        <canvas></canvas>
      </section>
      <section class="previewMenu floatingWindow hidden">
        <p class="buttons">
          <svg class="button" data-name="delete" viewBox="0 0 473 473"><use href="#svgDelete"></use></svg>
        </p>
        <p class="inputs">
          <label><input type="checkbox" name="smooth">Smooth</label>
        </p>
      </section>
    `;
    mulLangTexts.replaceTextNode(this._elWrapper);
    autoFocus.add(this._elWrapper);
    autoFocus.raise(this._elWrapper);
    this._elContainer = document.getElementById('windowContainer');
    this._elContainer.appendChild(this._elWrapper);
    this._elSection = this._elWrapper.querySelector('.exampleWindow');
    this._menu = new PopupMenu(this._elWrapper.querySelector('.previewMenu'), (name, target) => this._exec(name, target));
    this._grabbableWindow = new GrabbableWindow(this._elSection, null);

    this._elCanvas = this._elSection.querySelector('canvas');
    this._elCanvas.width = 256;
    this._elCanvas.height = Math.floor(this._image.height * this._elCanvas.width / this._image.width);
    this._elCanvas.style.imageRendering = 'pixelated';

    // DragCameraの設定
    this._dragCamera = new DragCamera(this._updateCanvas.bind(this), this._elCanvas);
    this._dragCamera.zoom = this._elCanvas.width / this._image.width;
    this._dragCamera.zoomMin = Math.min(.1, this._dragCamera.zoom);
    this._dragCamera.zoomMax = Math.max(10, this._dragCamera.zoom);
    this._dragCamera.setOuterLimit(this._elCanvas.width, this._elCanvas.height, this._image.width, this._image.height);
    
    this._updateCanvas();

    this._canvasEyedropper = new CanvasEyedropper(this._elCanvas, colorManager, eyedropperPopup);

    // DragToolUsableの設定
    this._dragToolUsable = new DragToolUsable(this._elSection, buttons, (x, y) => {
      const posName = this._getPosName(x, y);
      if(posName !== 'other') {
        const usingDragTool = new DragResizer(
          this._elCanvas, this._elSection, posName, ExampleWindow.MIN_SIZE,
          () => this._updateCanvas(), () => this._dragCamera.setOuterLimit(this._elCanvas.width, this._elCanvas.height, this._image.width, this._image.height)
        );
        this._startPosX = this._dragCamera.posX;
        this._startPosY = this._dragCamera.posY;
        usingDragTool.onResizeLeft = dx => this._dragCamera.posX = this._startPosX + this._dragCamera.localSzToWorldSz(dx);
        usingDragTool.onResizeTop = dy => this._dragCamera.posY = this._startPosY + this._dragCamera.localSzToWorldSz(dy);
        usingDragTool.start(x, y);
        this._hideMenu();
        return usingDragTool;
      }
      return null;
    }, (x, y) => {
      const usingDragTool = this._dragCamera;
      usingDragTool.start(x, y);
      this._elSection.style.cursor = 'var(--cursor-move)';
      return usingDragTool;
    }, (x, y) => {
      const usingDragTool = this._canvasEyedropper;
      usingDragTool.start(x, y);
      this._elSection.style.cursor = 'var(--cursor-eyedropper)';
      return usingDragTool;
    }, () => {
      this._elSection.style.cursor = '';
    });

    //-- addEventListener
    this._elWrapper.addEventListener('mouseenter', () => {
      if(!this._grabbableWindow.isGrabbing) this._showMenu();
      this._keyboard.setTmpDragCamera(this._dragCamera, this._elWrapper);
    });
    this._elWrapper.addEventListener('mouseleave', () => {
      this._hideMenu();
    });
    this._elSection.addEventListener('mouseup', e => {
      if(e.button === 0) this._showMenu();
    });
    this._elSection.addEventListener('mousedown', e => {
      if(e.button === 0 && this._grabbableWindow.isGrabbing) this._hideMenu();
    });
    this._elSection.addEventListener('mousemove', e => {
      if(!this._dragToolUsable.isUsing()) {
        const x = DPR.mul(e.x);
        const y = DPR.mul(e.y);
        switch (this._getPosName(x, y)) {
          case 'lt':
            this._elSection.style.cursor = ImageResizer.getCursorCSSValue(ImageResizer.RESIZER_CURSOR_RADS[0]);
            this._grabbableWindow.enabled = false;
            break;
          case 'rt':
            this._elSection.style.cursor = ImageResizer.getCursorCSSValue(ImageResizer.RESIZER_CURSOR_RADS[2]);
            this._grabbableWindow.enabled = false;
            break;
          case 'lb':
            this._elSection.style.cursor = ImageResizer.getCursorCSSValue(ImageResizer.RESIZER_CURSOR_RADS[6]);
            this._grabbableWindow.enabled = false;
            break;
          case 'rb':
            this._elSection.style.cursor = ImageResizer.getCursorCSSValue(ImageResizer.RESIZER_CURSOR_RADS[8]);
            this._grabbableWindow.enabled = false;
            break;
          default:
            this._elSection.style.cursor = '';
            this._grabbableWindow.enabled = true;
            break;
        }
      }
    });
    this._elWrapper.querySelector('input[name="smooth"]').addEventListener('change', e => {
      this._elCanvas.style.imageRendering = e.target.checked ? 'auto' : 'pixelated';
      this._updateCanvas();
    });
  }
  _showMenu() {
    const {x, y, width, height} = this._elSection.getBoundingClientRect();
    this._menu.move(x, y, width, height);
    this._menu.show();
  }
  _hideMenu() {
    this._menu.hide();
  }
  _exec(name, target) {
    switch(name) {
      case 'delete':
        if(confirm(this._mulLangTexts.get('Delete this example window?'))) {
          this._elWrapper.remove();
          this._ondestroy(this);
        }
        break;
    }
  }
  _updateCanvas() {
    this._elCanvas.width = this._elCanvas.width;
    const ctx = this._elCanvas.getContext('2d', {
      willReadFrequently: true,
    });
    ctx.clearRect(0, 0, this._elCanvas.width, this._elCanvas.height);
    const x = this._dragCamera.worldXToLocalX(0);
    const y = this._dragCamera.worldYToLocalY(0);
    const width = this._dragCamera.worldSzToLocalSz(this._image.width);
    const height = this._dragCamera.worldSzToLocalSz(this._image.height);
    ctx.imageSmoothingEnabled = this._elCanvas.style.imageRendering !== 'pixelated';
    ctx.drawImage(this._image, x, y, width, height);
  }
  _getPosName(x, y) {
    const rect = this._elSection.getBoundingClientRect();
    const inLeft = rect.x <= x && x < rect.x + ExampleWindow.GRABBER_SIZE;
    const inRight = rect.x + rect.width - ExampleWindow.GRABBER_SIZE <= x && x < rect.x + rect.width;
    const inTop = rect.y <= y && y < rect.y + ExampleWindow.GRABBER_SIZE;
    const inBottom = rect.y + rect.height - ExampleWindow.GRABBER_SIZE <= y && y < rect.y + rect.height;
    if(inLeft && inTop) {
      return 'lt';
    } else if(inRight && inTop) {
      return 'rt';
    } else if(inLeft && inBottom) {
      return 'lb';
    } else if(inRight && inBottom) {
      return 'rb';
    } else {
      return 'other';
    }
  }
}
